import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import {
  closedSupportResponse,
  isAvailable,
  isSupportApiResponse,
  SupportAvailability,
  SupportState,
  SUPPORT_ENDPOINT_PATH,
  SUPPORT_REQUEST_TIMEOUT_MS,
  toSupportState
} from 'Design-system/app/static/scripts/support-widget';

/**
 * Customer-support availability — the single source of truth for every support
 * surface in this app (exit intent #5622, and the floating widget #5620/#5621).
 *
 * The contract, the fail-closed fallback, the payload guard, and the snake_case →
 * camelCase mapping all live in `design-system` so this app and the Django SEO pages
 * cannot answer the same question differently. What stays here is the Redux shape:
 * the thunk, the slice, and the selector.
 *
 * Contract and rationale: `docs/support-widget-exit-intent-plan.md` §6 and the
 * endpoint's own docstring in `main.apis.public.v3.views.SupportViewV3`.
 *
 * Three rules this module exists to enforce, so no consumer has to remember them:
 *
 * 1. **Branch on `state` only.** `widget.*`, `flags.*` and `is_open` are published
 *    for surfaces that genuinely need an individual switch; recombining them into
 *    a second verdict puts the precedence rules in two places. `state` has already
 *    applied them.
 * 2. **Fail closed.** A non-200, a body that is not the expected JSON (Cloudflare
 *    bot management answers real visitors with an HTML challenge page) or a request
 *    that never lands all resolve to `offline`. No support entry point is better
 *    than one that does nothing.
 * 3. **Never send `fields[support-state]=`.** It would truncate the payload, and
 *    newly added config would be invisible to anyone who had not listed it.
 */

/** Re-exported so consumers in this app have one import path for support data. */
export type { SupportAvailability, SupportState };

/** The closed fallback. Every failure path is resolved here — see rule 2 above. */
export const CLOSED_SUPPORT_STATE: SupportState = toSupportState(closedSupportResponse());

const TIMED_OUT = Symbol('support-request-timeout');

export const getSupportState = createAsyncThunk(
  'settings/support/get',
  async (arg = undefined, api) => {
    let timer;

    const request = makeRequest(
      SUPPORT_ENDPOINT_PATH,
      {
        method: 'GET',
        // Anonymous endpoint: sending a JWT would make the answer uncacheable
        // per-user for no benefit.
        configure: { withCredentials: false }
      }
    )(api);

    // The request is not canceled — the shared API client takes no abort signal — we
    // simply stop waiting, so the UI settles into the closed fallback instead of
    // staying `loaded: false` forever.
    const timeout = new Promise<typeof TIMED_OUT>(resolve => {
      timer = setTimeout(() => resolve(TIMED_OUT), SUPPORT_REQUEST_TIMEOUT_MS);
    });

    try {
      const response = await Promise.race([request, timeout]);

      if (response === TIMED_OUT || !isSupportApiResponse(response?.data)) {
        return CLOSED_SUPPORT_STATE;
      }

      return toSupportState(response.data);
    } catch {
      // Never throw into the page — an unreachable support endpoint must not take
      // the job form down with it.
      return CLOSED_SUPPORT_STATE;
    } finally {
      clearTimeout(timer);
    }
  }
);

export interface SupportSliceState {
  loaded: boolean;
  entities: SupportState;
}

export const supportSlice = createSlice({
  name: 'support',
  initialState: {
    loaded: false,
    entities: CLOSED_SUPPORT_STATE
  } as SupportSliceState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSupportState.fulfilled, (state, action) => {
        state.entities = action.payload || CLOSED_SUPPORT_STATE;
        state.loaded = true;
      })
      // The thunk resolves the fallback rather than rejecting, so this only fires
      // on an unexpected throw. Settle into the same closed state either way.
      .addCase(getSupportState.rejected, state => {
        state.entities = CLOSED_SUPPORT_STATE;
        state.loaded = true;
      });
  }
});

/** The only availability check any surface should make. */
export const isSupportAvailable = (support: SupportSliceState): boolean =>
  support.loaded && isAvailable(support.entities.state);
